import React, { FCC } from 'react'
import styled, { css } from 'styled-components'
import { mix } from 'polished'

import { routes } from '@sketch/modules-common'
import { Flex, Link, showDropdownStyles } from '@sketch/components'

import Artboard from '../Artboard'
import PrototypeCardDropdown from '../PrototypeCardDropdown'

import { ReactComponent as BasePlayIcon } from '@sketch/icons/play-20'
import { LocationDescriptorObject } from 'history'
import { useVersioning } from '../../../versioning'
import { usePreview } from '../../views/PreviewView/PreviewContext'

type PlayButtonSize = 32 | 44

type PlayProps = {
  size: PlayButtonSize
}

const PlayButton = styled.span<PlayProps>(
  ({ theme, size }) => css`
    position: absolute;
    bottom: 10%;
    right: 10%;
    margin: auto;
    box-sizing: border-box;
    width: ${size}px;
    height: ${size}px;
    box-shadow: ${theme.colors.shadow.outer} 0 1px 2px 0;
    border-radius: ${size / 2}px;
    background-color: ${theme.colors.ui.buttons.prototypePlayButton};
    backdrop-filter: blur(10px);
    transition: all 200ms linear;

    :hover {
      background-color: ${mix(
        0.5,
        theme.colors.state.hover,
        theme.colors.ui.buttons.prototypePlayButton
      )};
    }
    :active {
      background-color: ${theme.colors.background.secondary.C};
    }
  `
)

const PlayIcon = styled(BasePlayIcon)`
  display: block;
  color: ${({ theme }) =>
    theme.colors.ui.buttons.prototypePlayButtonForeground};
  height: 17px;
  width: 17px;
  margin-left: 2px; /* stylelint-disable-line scales/space */
  margin-top: 2px; /* stylelint-disable-line scales/space */
`

const Prototype = styled(Artboard)`
  ${showDropdownStyles};
`

export interface PrototypeCardProps {
  artboardUUID: string
  title: string
  src: string
  showTitle?: boolean
  showDropdown?: boolean
  iconSize?: PlayButtonSize
}

const PrototypeCard: FCC<PrototypeCardProps> = ({
  artboardUUID,
  title,
  src,
  showTitle,
  showDropdown = true,
  iconSize = 44,
}) => {
  const { getPathname, share } = useVersioning()
  const previewContext = usePreview()

  const pathname: string = previewContext
    ? routes.PREVIEW_PROTOTYPE_PLAYER.create({
        previewID: previewContext.preview.identifier,
        prototypeArtboardUUID: artboardUUID,
        currentArtboardUUID: artboardUUID,
      })
    : getPathname({
        routeKey: 'PROTOTYPE_PLAYER',
        routeParams: {
          shareID: share.identifier,
          prototypeArtboardUUID: artboardUUID,
          currentArtboardUUID: artboardUUID,
        },
      })

  const to: LocationDescriptorObject = { pathname }

  return (
    <Link data-testid="prototype-card-link" to={to}>
      <Prototype
        title={title}
        imageSrc={src}
        showTitle={showTitle}
        overlay={
          <>
            {showDropdown && <PrototypeCardDropdown pathname={pathname} />}
            <PlayButton size={iconSize}>
              <Flex
                alignItems="center"
                justifyContent="center"
                height={iconSize}
                width={iconSize}
              >
                <PlayIcon />
              </Flex>
            </PlayButton>
          </>
        }
      />
    </Link>
  )
}

export default PrototypeCard
